import { Box, Button, CircularProgress, FormControl, FormHelperText, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { FC } from 'react';
import { Controller, FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { Alert } from '../../../../shared/lib/common/Alert';
import { BUTTON_TEXT_CANCEL, BUTTON_TEXT_SAVE, LABEL_SWAP_PRIORITY, LABEL_PRIORITY } from '../../../../shared/lib/constants';
import { MSG_PRIORITY_UPDATED } from '../../app/constants';
import palette from '../../../../shared/lib/theme/palette';
import { useAppContext } from '../../app/contexts/AppContext';
import { ChangeContactPriorityData, DeleteContactsProps } from '../../app/interfaceTypes';
import { ActionType } from '../../app/reducer/AppContextReducer';
import { createArrayWithoutExcludedNumber } from '../../app/utils';
import { Contact, useSwapContactPriorityMutation } from '../../generated';
import { CustomModal } from '../common/CustomModal';

export const ChangeContactPriority: FC<DeleteContactsProps> = ({ currentCardData, refetch }) => {
  const { isChangePriority, dispatch, cardsData } = useAppContext();
  const { id: currentId, priority } = currentCardData ?? {};
  const { borderLight } = palette;

  const methods = useForm<ChangeContactPriorityData, any, ChangeContactPriorityData>({
    mode: 'all',

    defaultValues: {
      // newPriority: priority
    }
  });

  const { handleSubmit, control, reset } = methods;

  const handleClose = () => {
    reset();
    dispatch({ type: ActionType.SET_IS_CHANGE_PRIORITY, isChangePriority: false });
  };

  const [swapContactPriority, { loading }] = useSwapContactPriorityMutation({
    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { swapContactPriority } = data ?? {};

      if (swapContactPriority) {
        Alert.success(MSG_PRIORITY_UPDATED);
        dispatch({ type: ActionType.SET_CARDS_DATA, cardsData: swapContactPriority });
        handleClose();
        reset();
        refetch();
      }
    }
  });

  const onSubmit: SubmitHandler<ChangeContactPriorityData> = async (data) => {
    const { newPriority } = data;
    const overContact = cardsData.filter((card: Contact) => card.priority === newPriority);

    currentId &&
      priority &&
      (await swapContactPriority({
        variables: {
          swapContactPriorityInput: {
            idOne: currentId,
            idTwo: overContact[0].id,
            priorityOne: priority,
            priorityTwo: newPriority
          }
        }
      }));
  };

  return (
    <CustomModal maxWidth="500px" isOpen={isChangePriority} handleClose={handleClose}>
      <Box padding="0 8px">
        <Box position="relative">
          <Typography variant="h6" sx={{ fontWeight: '700', color: 'blackOne', padding: '24px 0' }}>
            {LABEL_SWAP_PRIORITY}
          </Typography>
        </Box>

        <FormProvider {...methods}>
          <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            {priority && (
              <Controller
                name="newPriority"
                control={control}
                rules={{
                  required: 'Priority is required'
                }}
                render={({ field, fieldState }) => (
                  <FormControl sx={{ marginTop: '8px', width: '190px' }} variant="outlined" error={!!fieldState.error}>
                    <InputLabel>{LABEL_PRIORITY}</InputLabel>
                    <Select {...field} label={LABEL_PRIORITY}>
                      {createArrayWithoutExcludedNumber(cardsData.length, priority).map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            )}

            <Box display="flex" width="100%" gap="10px" mt="24px" mb="8px">
              <Button fullWidth onClick={handleClose} variant="text" sx={{ border: `1px solid ${borderLight}` }}>
                {BUTTON_TEXT_CANCEL}
              </Button>
              <Button fullWidth type="submit" variant="contained" color="primary" disabled={loading} endIcon={loading && <CircularProgress size={20} color="inherit" />}>
                {BUTTON_TEXT_SAVE}
              </Button>
            </Box>
          </form>
        </FormProvider>
      </Box>
    </CustomModal>
  );
};
